import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import { Printer, Search, RefreshCw, Calendar, Filter, ArrowUpDown, AlertCircle, Clock, Package, User, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function LabelReprintManagement() {
  const [prints, setPrints] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [printType, setPrintType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const loadPrints = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '25'
      });
      
      if (search) params.append('search', search);
      if (printType !== 'all') params.append('print_type', printType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/label-prints?${params}`);
      setPrints(response.data.data || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load label prints:', error);
      toast.error('Failed to load label print history');
    } finally {
      setLoading(false);
    }
  }, [page, search, printType, startDate, endDate]);
  
  const loadSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/label-prints/summary?${params}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  }, [startDate, endDate]);
  
  useEffect(() => {
    loadPrints();
    loadSummary();
  }, [loadPrints, loadSummary]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadPrints();
    loadSummary();
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IE', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="space-y-6" data-testid="label-reprint-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Printer className="w-6 h-6 text-blue-600" />
            Label Reprint Management
          </h1>
          <p className="text-muted-foreground">Track and audit all label prints and reprints</p>
        </div>
        <Button variant="outline" onClick={() => { loadPrints(); loadSummary(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Prints</p>
                  <p className="text-3xl font-bold">{summary.total_prints}</p>
                </div>
                <Printer className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Original Prints</p>
                  <p className="text-3xl font-bold text-green-600">{summary.total_original}</p>
                </div>
                <FileText className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Reprints</p>
                  <p className="text-3xl font-bold text-amber-600">{summary.total_reprints}</p>
                </div>
                <RefreshCw className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Reprint Rate</p>
                  <p className="text-3xl font-bold">{summary.reprint_percentage}%</p>
                </div>
                <AlertCircle className={`w-10 h-10 ${summary.reprint_percentage > 10 ? 'text-red-200' : 'text-gray-200'}`} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Top Reasons & Products */}
      {summary && (summary.top_reasons?.length > 0 || summary.top_products_by_reprints?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {summary.top_reasons?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Top Reprint Reasons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {summary.top_reasons.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{r.reason}</span>
                      <Badge variant="secondary">{r.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {summary.top_products_by_reprints?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Top Products by Reprints</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {summary.top_products_by_reprints.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{p.product_name || 'Unknown'}</span>
                      <Badge variant="destructive">{p.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <Input
                placeholder="Search by dispatch #, product, batch, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mt-1"
                data-testid="label-search-input"
              />
            </div>
            
            <div className="w-[150px]">
              <Label className="text-xs">Type</Label>
              <Select value={printType} onValueChange={setPrintType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="original">Original</SelectItem>
                  <SelectItem value="reprint">Reprint</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[150px]">
              <Label className="text-xs">From Date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="From date"
              />
            </div>
            
            <div className="w-[150px]">
              <Label className="text-xs">To Date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="To date"
              />
            </div>
            
            <Button type="submit" data-testid="btn-search-labels">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>
        </CardContent>
      </Card>
      
      {/* Print History Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Print History
            </CardTitle>
            <span className="text-sm text-muted-foreground">{total} records</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : prints.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Printer className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No label prints found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-medium">Date/Time</th>
                    <th className="p-3 text-left font-medium">Type</th>
                    <th className="p-3 text-left font-medium">Dispatch #</th>
                    <th className="p-3 text-left font-medium">Product</th>
                    <th className="p-3 text-left font-medium">Batch</th>
                    <th className="p-3 text-left font-medium">Customer</th>
                    <th className="p-3 text-left font-medium">Qty</th>
                    <th className="p-3 text-left font-medium">Printed By</th>
                    <th className="p-3 text-left font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {prints.map((print) => (
                    <tr key={print.id} className={`border-b hover:bg-muted/30 ${print.print_type === 'reprint' ? 'bg-amber-50/50' : ''}`}>
                      <td className="p-3 whitespace-nowrap">
                        <span className="text-xs">{formatDate(print.printed_at)}</span>
                      </td>
                      <td className="p-3">
                        <Badge variant={print.print_type === 'reprint' ? 'destructive' : 'secondary'}>
                          {print.print_type}
                        </Badge>
                      </td>
                      <td className="p-3 font-mono">{print.dispatch_number || '-'}</td>
                      <td className="p-3">
                        <div className="flex flex-col">
                          <span className="font-medium">{print.product_name?.slice(0, 30)}</span>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs">{print.batch_code || '-'}</td>
                      <td className="p-3 text-xs">{print.customer_name?.slice(0, 20) || '-'}</td>
                      <td className="p-3 text-center">{print.quantity_printed}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs">{print.printed_by_email?.split('@')[0] || '-'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {print.reason ? (
                          <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded">
                            {print.reason.slice(0, 30)}{print.reason.length > 30 ? '...' : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
